import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Skeleton } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { TENANT } from '../../../tenant'
import { TosSuperscript } from '../../../tos-superscripts'
import { crossSellGetFormattedPrice } from '../../crossSellGetFormattedPrice'
import { crossSellAccessorGetProductDiscount } from '../../model/crossSellAccessorGetProductDiscount'

type CrossSellHorizontalSliderItemCompactPriceProps = {
  product?: CrossSellProduct
}

const superscriptPattern = /\p{No}/gu

export const CrossSellHorizontalSliderItemCompactPrice: FC<
  CrossSellHorizontalSliderItemCompactPriceProps
> = ({ product }) => {
  const { superScripts, tenant } = useGlobalConfigContext()
  const intl = useIntl()

  if (!product) {
    return (
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full gap-2">
          <Skeleton className="w-2/5" />
          <Skeleton className="w-2/5" />
        </div>
        <Skeleton className="w-3/5" />
        <div className="flex w-full gap-2">
          <Skeleton className="w-2/5" />
          <Skeleton className="w-2/5" />
        </div>
      </div>
    )
  }

  const discount = crossSellAccessorGetProductDiscount(product)

  return (
    <div className="flex flex-col items-start pt-1">
      <div className="mb-1 flex flex-row items-center">
        {discount ? (
          <div className="mr-2.5 flex min-w-12 items-center justify-center rounded bg-dark-brand p-2 text-xs font-bold leading-none text-light-primary-strong">
            {discount}
          </div>
        ) : null}
        <span className="min-h-[var(--type-scale-s-line-height)] whitespace-nowrap py-0.5 text-m font-bold text-dark-error">
          {crossSellGetFormattedPrice(product.price, intl)}
        </span>
      </div>
      <div className="min-h-[var(--type-scale-s-line-height)] text-dark-primary-strong">
        {crossSellGetFormattedPrice(product.new_baseprice, intl)}
      </div>
      <span className="flex gap-2">
        {product.reduced === 'true' && (
          <span className="truncate">
            <FormattedMessage id="general.label.listPrice" />
            <TosSuperscript value={superScripts.avp} />
            <span className="ml-2 line-through">
              {crossSellGetFormattedPrice(
                product.oldprice.replaceAll(superscriptPattern, ''),
                intl,
                {
                  // on IT superscript is not in response, we need to force adding it for oldprice
                  isItalianStrikethrough: tenant === TENANT.IT,
                },
              )}
            </span>
          </span>
        )}
      </span>
    </div>
  )
}
